/**
 * ============================================================================
 * Iron Meridian - Connection Status Service
 * ============================================================================
 * 
 * PURPOSE:
 * Tracks and exposes the current connection/persistence status.
 * Emits events when status changes for reactive UI updates.
 * 
 * STATUS VALUES:
 * - LOCAL: Using localStorage only (legacy/fallback)
 * - CONNECTED: Active D1 database connection
 * - ONLINE: Multiplayer mode (future)
 * - OFFLINE: No connection / error state
 * 
 * ============================================================================
 */

// ============================================================================
// CONNECTION STATUS ENUM
// ============================================================================

export const CONNECTION_STATUS = {
  LOCAL: 'local',        // Blue - localStorage fallback
  CONNECTED: 'connected', // Green - D1 database active
  ONLINE: 'online',       // Yellow/Gold - Multiplayer (future)
  OFFLINE: 'offline'      // Red - Error/no connection
}

// ============================================================================
// CONNECTION STATUS SERVICE
// ============================================================================

class ConnectionStatusService {
  constructor() {
    this._status = CONNECTION_STATUS.LOCAL
    this._listeners = new Set()
    this._lastCheck = null
    this._checkInterval = null
    this._dbAvailable = false
    this._isOnline = navigator?.onLine ?? true
    
    // Listen to browser online/offline events
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this._handleOnline())
      window.addEventListener('offline', () => this._handleOffline())
      
      // Check immediately on page load
      this._checkConnection()
    }
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /**
   * Get current connection status
   * @returns {string} One of CONNECTION_STATUS values
   */
  getStatus() {
    return this._status
  }

  /**
   * Check if database is available
   * @returns {boolean}
   */
  isDatabaseAvailable() {
    return this._dbAvailable
  }

  /**
   * Check if browser is online
   * @returns {boolean}
   */
  isOnline() {
    return this._isOnline
  }

  /**
   * Subscribe to status changes
   * @param {Function} callback - Called with new status
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this._listeners.add(callback)
    return () => this._listeners.delete(callback)
  }

  /**
   * Update the connection status based on adapter type and DB availability
   * @param {Object} options
   * @param {string} options.adapterType - 'local', 'sqlite', or 'remote'
   * @param {boolean} options.dbAvailable - Whether D1 is available
   * @param {boolean} [options.isMultiplayer=false] - Whether in multiplayer mode
   */
  updateStatus({ adapterType, dbAvailable, isMultiplayer = false }) {
    this._dbAvailable = dbAvailable

    let newStatus

    // Determine status based on current state
    if (!this._isOnline) {
      newStatus = CONNECTION_STATUS.OFFLINE
    } else if (isMultiplayer) {
      newStatus = CONNECTION_STATUS.ONLINE
    } else if (adapterType === 'sqlite' && dbAvailable) {
      newStatus = CONNECTION_STATUS.CONNECTED
    } else {
      newStatus = CONNECTION_STATUS.LOCAL
    }

    if (newStatus !== this._status) {
      this._status = newStatus
      this._emit(newStatus)
    }
  }

  /**
   * Report a connection error
   */
  reportError() {
    if (this._status !== CONNECTION_STATUS.OFFLINE) {
      this._status = CONNECTION_STATUS.OFFLINE
      this._emit(this._status)
    }
  }

  /**
   * Report connection restored
   * @param {boolean} dbAvailable - Whether D1 is available
   */
  reportRestored(dbAvailable = false) {
    this._dbAvailable = dbAvailable
    const newStatus = dbAvailable ? CONNECTION_STATUS.CONNECTED : CONNECTION_STATUS.LOCAL
    if (this._status !== newStatus) {
      this._status = newStatus
      this._emit(newStatus)
    }
  }

  /**
   * Start periodic status checks
   * @param {number} intervalMs - Check interval in milliseconds
   */
  startPeriodicCheck(intervalMs = 30000) {
    if (this._checkInterval) return
    this._checkInterval = setInterval(() => this._checkConnection(), intervalMs)
  }

  /**
   * Stop periodic status checks
   */
  stopPeriodicCheck() {
    if (this._checkInterval) {
      clearInterval(this._checkInterval)
      this._checkInterval = null
    }
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  _emit(status) {
    for (const listener of this._listeners) {
      try {
        listener(status)
      } catch (err) {
        console.error('[ConnectionStatus] Listener error:', err)
      }
    }
  }

  _handleOnline() {
    this._isOnline = true
    // Will update status on next check or when persistence reports success
    this._checkConnection()
  }

  _handleOffline() {
    this._isOnline = false
    this._status = CONNECTION_STATUS.OFFLINE
    this._emit(this._status)
  }

  async _checkConnection() {
    if (!this._isOnline) return

    try {
      // Ping the DB init endpoint to check availability
      const response = await fetch('/api/db/init', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })

      if (response.ok) {
        const result = await response.json()
        this.reportRestored(result.data?.available ?? false)
      } else {
        this.reportRestored(false)
      }
    } catch {
      // Network error but browser thinks we're online
      this.reportRestored(false)
    }

    this._lastCheck = Date.now()
  }
}

// ============================================================================
// SINGLETON EXPORT
// ============================================================================

export const connectionStatus = new ConnectionStatusService()
export default connectionStatus
